// src/components/Layout.tsx - FIXED: Removed geofencing from dark mode override
import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Map as MapIcon,
  DirectionsCar as VehiclesIcon,
  Assignment as RequestIcon,
  CarRental as FleetIcon,
  ExpandLess,
  ExpandMore,
  AccountCircle,
  Logout,
  Timeline,
  People as PeopleIcon,
  Shield as ShieldIcon,
  Menu as MenuIcon,
  Settings as AdminIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeContext } from '../contexts/ThemeContext';
import { MenuItem as MenuItemType, SubMenuItem, LayoutProps } from '../types';
import NotificationBell from './NotificationBell';
import ThemeSettingsButton from './ThemeSettingsButton';

const drawerWidth = 240;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dashboardOpen, setDashboardOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoKey, setLogoKey] = useState(0);
  const { user, company, logout, isRole } = useAuth();
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    if (!mobileOpen) {
      setLogoKey(prev => prev + 1);
    }
  };

  const getLogoPath = () => {
    if (company?.logo) {
      if (company.logo.startsWith('http://') || company.logo.startsWith('https://')) {
        return company.logo;
      }
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:1001').replace('/api', '');
      const logoUrl = `${baseUrl}${company.logo}`;
      console.log('🖼️ Logo URL generated:', logoUrl);
      return logoUrl;
    }
    return mode === 'dark' ? '/Scetru-Logo-white.png' : '/Scetru-Logo-black.png';
  };

  const menuItems: MenuItemType[] = [
    {
      text: 'Dashboard & Analytics',
      icon: <DashboardIcon />,
      path: '/dashboard',
      hasSubItems: true,
      subItems: [
        { text: 'Dashboard', path: '/dashboard' },
        { text: 'Analytics', path: '/analytics' }
      ]
    },
    {
      text: 'Map',
      icon: <MapIcon />,
      path: '/map',
      hasSubItems: false
    },
    {
      text: 'Trip Playback',
      icon: <Timeline />,
      path: '/trip-playback',
      hasSubItems: false,
      requiredRoles: ['super_admin', 'admin', 'fleet_manager']
    },
    {
      text: 'Vehicles & Assets',
      icon: <VehiclesIcon />,
      path: '/devices',
      hasSubItems: false
    },
    {
      text: 'Fleet Management',
      icon: <FleetIcon />,
      path: '/fleet-management',
      hasSubItems: false
    },
    {
      text: 'Vehicle Requests',
      icon: <RequestIcon />,
      path: '/vehicle-requests',
      hasSubItems: false
    },
    {
      text: 'Drivers',
      icon: <PeopleIcon />,
      path: '/drivers',
      hasSubItems: false
    },
    {
      text: 'Geofencing',
      icon: <ShieldIcon />,
      path: '/geofencing',
      hasSubItems: false,
      requiredRoles: ['super_admin', 'admin', 'fleet_manager']
    },
    {
      text: 'Administration',
      icon: <AdminIcon />,
      path: '/admin',
      hasSubItems: true,
      requiredRoles: ['super_admin', 'fleet_manager'],
      subItems: [
        { text: 'Users', path: '/admin/users', roles: ['super_admin', 'fleet_manager'] },
        { text: 'Company Settings', path: '/admin/company', roles: ['super_admin'] },
      ]
    },
  ];

  const drawer = (
    <div style={{ width: '100%' }}>
      <Box sx={{
        p: isMobile ? 1 : 1.5,
        borderBottom: '1px solid',
        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        bgcolor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        minHeight: isMobile ? '60px' : '75px',
        width: '100%',
        boxSizing: 'border-box',
      }}>
        <img
          key={`logo-${logoKey}-${isMobile}`}
          src={getLogoPath()}
          alt={company?.name || 'Scetru'}
          style={{
            height: isMobile ? '45px' : '50px',
            width: 'auto',
            maxWidth: '95%',
            objectFit: 'contain',
            display: 'block',
            visibility: 'visible',
            margin: '0 auto',
            filter: company?.logo && mode === 'dark'
              ? 'brightness(1.2) contrast(0.9)'
              : 'none',
          }}
          onError={(e) => {
            console.error('❌ Logo failed to load, using fallback');
            (e.target as HTMLImageElement).src = mode === 'dark'
              ? '/Scetru-Logo-white.png'
              : '/Scetru-Logo-black.png';
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: mode === 'dark' ? '#b0b0b0' : 'rgba(0,0,0,0.6)',
            textAlign: 'center',
            fontSize: isMobile ? '0.7rem' : '0.75rem',
            width: '100%',
            letterSpacing: 0.3,
            lineHeight: 1.2,
          }}
        >
          {company?.name || 'Fleet Management'}
        </Typography>
      </Box>

      <List sx={{ pt: 1 }}>
        {menuItems
          .filter(item => !item.requiredRoles || item.requiredRoles.some((role: string) => isRole(role)))
          .map((item) => (
            <React.Fragment key={item.text}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path || (item.hasSubItems && item.subItems?.some(sub => location.pathname === sub.path))}
                  onClick={() => {
                    if (item.hasSubItems) {
                      if (item.text === 'Dashboard & Analytics') {
                        setDashboardOpen(!dashboardOpen);
                      }
                    } else {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }
                  }}
                  sx={{
                    color: mode === 'dark' ? '#ffffff' : '#000000',
                    '& .MuiListItemIcon-root': {
                      color: mode === 'dark' ? '#ffffff' : 'rgba(0,0,0,0.7)',
                    },
                    '& .MuiListItemText-primary': {
                      color: mode === 'dark' ? '#ffffff' : '#000000',
                    },
                    '&.Mui-selected': {
                      backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#e6f2ff',
                      borderRight: '3px solid',
                      borderRightColor: 'primary.main',
                      '& .MuiListItemIcon-root': {
                        color: 'primary.main',
                      },
                      '& .MuiListItemText-primary': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                    },
                    '&:hover': {
                      backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.875rem'
                    }}
                  />
                  {item.hasSubItems && (
                    item.text === 'Dashboard & Analytics' ?
                      (dashboardOpen ? <ExpandLess /> : <ExpandMore />) : null
                  )}
                </ListItemButton>
              </ListItem>

              {item.hasSubItems && item.text === 'Dashboard & Analytics' && (
                <Collapse in={dashboardOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems?.map((subItem: SubMenuItem) => (
                      <ListItemButton
                        key={subItem.text}
                        sx={{
                          pl: 4,
                          color: mode === 'dark' ? '#ffffff' : '#000000',
                          '& .MuiListItemText-primary': {
                            color: mode === 'dark' ? '#ffffff' : '#000000',
                          },
                          '&.Mui-selected': {
                            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                            '& .MuiListItemText-primary': {
                              color: 'primary.main',
                              fontWeight: 600,
                            },
                          },
                          '&:hover': {
                            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                          },
                        }}
                        selected={location.pathname === subItem.path}
                        onClick={() => {
                          navigate(subItem.path);
                          if (isMobile) setMobileOpen(false);
                        }}
                      >
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            fontSize: '0.8rem'
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}

              {item.hasSubItems && item.text === 'Administration' && (
                <Collapse in={true} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems
                      ?.filter((subItem: SubMenuItem) => !subItem.roles || subItem.roles.some((role: string) => isRole(role)))
                      ?.map((subItem: SubMenuItem) => (
                        <ListItemButton
                          key={subItem.text}
                          sx={{
                            pl: 4,
                            color: mode === 'dark' ? '#ffffff' : '#000000',
                            '& .MuiListItemText-primary': {
                              color: mode === 'dark' ? '#ffffff' : '#000000',
                            },
                            '&.Mui-selected': {
                              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                              '& .MuiListItemText-primary': {
                                color: 'primary.main',
                                fontWeight: 600,
                              },
                            },
                            '&:hover': {
                              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                            },
                          }}
                          selected={location.pathname === subItem.path}
                          onClick={() => {
                            navigate(subItem.path);
                            if (isMobile) setMobileOpen(false);
                          }}
                        >
                          <ListItemText
                            primary={subItem.text}
                            primaryTypographyProps={{
                              fontSize: '0.8rem'
                            }}
                          />
                        </ListItemButton>
                      ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'primary.main',
          boxShadow: '0 2px 8px rgba(0,31,63,0.15)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: '#ffffff !important',
          '& .MuiIconButton-root': {
            color: '#ffffff !important',
          },
          '& .MuiTypography-root': {
            color: '#ffffff !important',
          },
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ThemeSettingsButton />
            <NotificationBell />

            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                display: { xs: 'none', md: 'block' },
                color: '#ffffff !important',
              }}
            >
              {user?.firstName} {user?.lastName}
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'rgba(255,255,255,0.7) !important' }}>
                ({user?.role?.replace('_', ' ')})
              </Typography>
            </Typography>
            <IconButton
              size="large"
              onClick={handleMenuOpen}
              sx={{ color: '#ffffff !important' }}
            >
              <AccountCircle />
            </IconButton>
          </Box>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
                color: mode === 'dark' ? '#ffffff' : '#000000',
              },
              '& .MuiMenuItem-root': {
                color: mode === 'dark' ? '#ffffff' : '#000000',
              },
            }}
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {user?.firstName} {user?.lastName}
              </Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="body2" sx={{ color: mode === 'dark' ? '#b0b0b0' : 'rgba(0,0,0,0.6)' }}>
                {user?.email}
              </Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="body2" sx={{ color: mode === 'dark' ? '#b0b0b0' : 'rgba(0,0,0,0.6)' }}>
                {company?.name}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon sx={{ color: mode === 'dark' ? '#ffffff' : 'rgba(0,0,0,0.7)' }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
              backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* ✅ FIXED: Removed /geofencing from dark mode override */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: location.pathname === '/map'
            ? '#1a1a1a !important'
            : mode === 'dark' ? '#121212 !important' : '#f5f5f5 !important',
          color: location.pathname === '/map'
            ? '#ffffff !important'
            : mode === 'dark' ? '#ffffff !important' : '#000000 !important',
          '& .MuiPaper-root': location.pathname === '/map'
            ? { backgroundColor: 'rgba(45, 45, 45, 0.9) !important', color: '#ffffff !important' }
            : mode === 'dark'
              ? { backgroundColor: '#1e1e1e !important', color: '#ffffff !important' }
              : { backgroundColor: '#ffffff !important', color: '#000000 !important' },
          '& .MuiCard-root': location.pathname === '/map'
            ? { backgroundColor: 'rgba(45, 45, 45, 0.9) !important', color: '#ffffff !important' }
            : mode === 'dark'
              ? { backgroundColor: '#1e1e1e !important', color: '#ffffff !important' }
              : { backgroundColor: '#ffffff !important', color: '#000000 !important' },
          '& .MuiTableHead-root': {
            backgroundColor: location.pathname === '/map'
              ? '#2a2a2a !important'
              : mode === 'dark' ? '#2a2a2a !important' : '#f0f4f8 !important'
          },
          '& .MuiTableCell-root': {
            color: location.pathname === '/map'
              ? '#ffffff !important'
              : mode === 'dark' ? '#ffffff !important' : '#000000 !important'
          },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;